import { ChoiceList, TextField } from "@cmsgov/ds-medicare-gov";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import Div from "../../common/components/div";
import H1 from "../../common/components/h1";
import translate from "../../common/functions/translate";
import { SubmitButton } from "../buttons/Buttons";
import { MfaHelpDrawer } from "../shared-components/MfaHelpDrawer";
import { useValidateEmail } from "../hooks/useValidateEmail";
import { MfaEnrollStep1Response, MfaEnrollStep2Response, SecurityCodeMethod, useMfaApi } from "../hooks/useMfaApi";
import { SmartErrorAlert } from "../../common/components/SmartErrorAlert";
import { useMfaInlineError } from "../../common/hooks/use.analytics";
import { AxiosResponse } from "axios";
import { VerifyPassword } from "../VerifyPassword";
import { getMfaDuplicateChannelError } from "../../common/functions/getApiErrorMessage";

export interface SelectEmailProps {
    value: string | null
    flow: 'enroll' | 'add'
}

type Options = 'hasEmail' | 'updateEmail';

export const SelectEmail = ({ value, flow }: SelectEmailProps) => {
    const location = useLocation<{
        type: SecurityCodeMethod,
        value?: string
    }>();
    const methodValue = value || location.state.value;
    const [isLoading, setIsLoading] = useState(false);
    const [showEmailInput, setShowEmailInput] = useState(false);
    const [option, setOption] = useState<Options | null>(null);
    const [email, setEmail] = useState('');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [emailRef, setEmailRef] = useState<HTMLElement | null>(null);  // removing this line breaks tests, unclear why
    const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);
    const [fieldVisited, setFieldVisited] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [apiResponse, setApiResponse] = useState<AxiosResponse<MfaEnrollStep1Response | MfaEnrollStep2Response> | null>(null);
    const [isVerified, setIsVerified] = useState(true);
    const [isDuplicateEmail, setIsDuplicateEmail] = useState(false);
    const history = useHistory();
    const api = useMfaApi();
    const emailErrorState = useValidateEmail(email);
    const type = 'email';

    const choiceLabel = translate("select_email_address");
    const choiceInlineErr = useMfaInlineError(choiceLabel);
    const emailFieldLabel = translate("enter_new_email");
    const inputInlineErr = useMfaInlineError(emailFieldLabel);

    useEffect(() => {
        if (option !== 'updateEmail') {
            setShowEmailInput(false);
            return;
        }
        setShowEmailInput(true);
    }, [option, setEmail]);

    async function onSubmit(event?: React.FormEvent<HTMLFormElement>) {
        event?.preventDefault();
        setFormSubmitAttempted(true);
        setApiResponse(null);
        setIsVerified(true);
        const noSelection = !option;
        const emailError = (option === 'updateEmail' && emailErrorState) || isDuplicateEmail;
        if (isLoading || emailError || noSelection) {
            if (noSelection) {
                choiceInlineErr.validationError(translate("select_your_preferred_email"));
            }
            if (emailError) {
                inputInlineErr.validationError(getInputErrMessage())
            }
            return;
        }
        const submittedValue = (option === 'hasEmail') ? methodValue! : email;
        setIsLoading(true);
        const mfaStepResponse = await api.mfaEnrollStep1({
            type,
            value: submittedValue,
        });
        const code = mfaStepResponse?.data.code;
        setApiResponse(mfaStepResponse);
        setIsLoading(false);

        const errors = mfaStepResponse?.data.errors || [];
        // set inline error for duplicate email if applicable
        const duplicateError = getMfaDuplicateChannelError(errors, 'email');
        if (duplicateError) {
            inputInlineErr.serverError(duplicateError, 'MFA_DUPLICATE_CHANNEL');
        }
        setErrorMessage(duplicateError);
        setIsDuplicateEmail(!!duplicateError);

        if (code === 401) {
            setIsVerified(false);
            return;
        }
        if (code === 201) {
            history.push("/security-code/enroll/confirm-your-method", {
                value: submittedValue,
                type: 'email',
                flow: 'enroll'
            });
        }
    }

    function updateOptionSelected(selection: Options) {
        setOption(selection);
    }

    function updateEmail(emailInputValue: string) {
        setEmail(emailInputValue);
        setIsDuplicateEmail(false);
    }

    function updateValidateEmail() {
        setFieldVisited(true);
    }

    function getChoicesErrorMessage() {
        const isFirstLoad = (!option && !formSubmitAttempted);
        if (isFirstLoad || option === 'hasEmail') {
            return;
        }
        if (formSubmitAttempted && !option) {
            if(errorMessage !== translate("select_your_preferred_email")) {
                setErrorMessage(translate("select_your_preferred_email"));
            }
            return translate("select_your_preferred_email");
        }
    }

    const getInputErrMessage = () => {
        if (emailErrorState === 'empty') {
            if(errorMessage !== translate("enter_your_new_email_address")) {
                setErrorMessage(translate("enter_your_new_email_address"));
            }
            return translate("enter_your_new_email_address");
        }
        if (emailErrorState === 'invalid_length') {
            return translate("the_email_length_is_invalid");
        }
        if (emailErrorState === 'invalid') {
            if(errorMessage !== translate("the_email_is_invalid")) {
                setErrorMessage(translate("the_email_is_invalid"));
            }
            return translate("the_email_is_invalid");
        }
        if (isDuplicateEmail) {
            return errorMessage; // already translated
        }
        return '';
    }

    function getInputErrorMessage() {
        if (option !== 'updateEmail') {
            return;
        }
        if (!fieldVisited && !formSubmitAttempted) {
            return;
        }
        return getInputErrMessage();
    }

    if (!isVerified) {
        return <VerifyPassword type={type} flow={flow} onVerified={onSubmit} />
    }

    const choiceListMargin = showEmailInput ? 'ds-u-margin-bottom--0': 'ds-u-margin-bottom--3';
    const headingText = translate(`mfa_title_${flow}`);
    return (<>
        <form onSubmit={onSubmit} method="POST">
            <Div isRow>
                <Div cols={12} colsMD={8}>
                    <H1 className={'ds-u-margin-bottom--1'}>{headingText}</H1>
                    <MfaHelpDrawer />
                </Div>
            </Div>
            <SmartErrorAlert
                className="ds-u-margin-top--0"
                name='MfaEnrollStep1Email'
                fieldRef={null}
                apiResponse={apiResponse}
                conversion={null}
                invalidFields={null}
                onInvalidField={() => {}}
                parentHeading={headingText}
                mfaFlow={flow}
                mfaIsAnotherCodeRequested={false}
            />
            <Div isRow>
                <Div cols={12} colsMD={8}>
                    <div className={choiceListMargin}>
                        <ChoiceList
                            label={<h2 className={"ds-u-margin-bottom--1 ds-text-heading--xl"} style={{ textTransform: 'none' }}>
                                {choiceLabel}
                            </h2>}
                            labelClassName={"ds-u-measure--wide"}
                            name={'selectSecurityMethod'}
                            onChange={(e) => updateOptionSelected(e.target.value)}
                            type={'radio'}
                            choices={[
                                {
                                    label: <>
                                        <span>{translate("email_me_at")}</span> <span className="ds-u-font-weight--bold ds-u-text-transform--uppercase">{methodValue}</span>
                                    </>,
                                    value: `hasEmail`
                                },
                                {
                                    label: translate('use_different_email_address'),
                                    value: 'updateEmail',
                                    hint: translate('getting_codes_from_account')
                                }
                            ]}
                            errorMessage={getChoicesErrorMessage()}
                        />
                    </div>
                    <div className="ds-u-margin-left--5">
                        {showEmailInput && <TextField
                            labelClassName="ds-u-margin-top--1"
                            className={'ds-u-margin-bottom--3'}
                            aria-invalid={emailErrorState === null}
                            errorMessage={getInputErrorMessage()}
                            inputRef={setEmailRef}
                            hint={translate("hint_text")}
                            label={emailFieldLabel}
                            name={'emailAddress'}
                            onChange={event => updateEmail(event.currentTarget.value)}
                            onBlur={updateValidateEmail}
                            value={email}
                        />}
                    </div>
                    <SubmitButton isLoading={isLoading} mfaAnalyticsData={{
                        text: translate("send_my_code_email"),
                        parentComponentHeading: headingText
                    }}>
                        {translate("send_my_code_email")}
                    </SubmitButton>
                </Div>
            </Div>
        </form>
    </>
    );
}